import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../styles/AdminPage.css';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { uploadWatchImage } from '../utils/uploadWatchImage';
import { withCropParams } from '../utils/imageUrl';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState('');
  
  // Form state for adding/editing watches
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentWatch, setCurrentWatch] = useState(null);
  const [watchForm, setWatchForm] = useState({
    name: '',
    description: '',
    longDescription: '',
    price: '',
    image1: '',
    image2: '',
    image3: '',
    image4: ''
  });
  
  // Load watches when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchWatches();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!uploadSuccessMessage) return;
    const t = setTimeout(() => setUploadSuccessMessage(''), 6000);
    return () => clearTimeout(t);
  }, [uploadSuccessMessage]);
  
  const fetchWatches = async () => {
    setLoading(true);
    const querySnapshot = await getDocs(collection(db, 'watches'));
    const watchesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setWatches(watchesData);
    setLoading(false);
  };
  
  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');
    
    // Hardcoded login credentials as requested
    if (username === 'efeadmin' && password === 'pennywise1404') {
      setIsAuthenticated(true);
    } else {
      setLoginError('Hatalı kullanıcı adı veya şifre');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };
  
  const handleAddWatch = () => {
    setIsEditing(false);
    setCurrentWatch(null);
    setWatchForm({
      name: '',
      description: '',
      longDescription: '',
      price: '',
      image1: '',
      image2: '',
      image3: '',
      image4: ''
    });
    setUploadError('');
    setUploadSuccessMessage('');
    setShowForm(true);
  };
  
  const handleEditWatch = (watch) => {
    setIsEditing(true);
    setCurrentWatch(watch);
    setWatchForm({
      name: watch.name,
      description: watch.description,
      longDescription: watch.longDescription || '',
      price: watch.price.replace('$', ''),
      image1: watch.images[0] || '',
      image2: watch.images[1] || '',
      image3: watch.images[2] || '',
      image4: watch.images[3] || ''
    });
    setUploadError('');
    setUploadSuccessMessage('');
    setShowForm(true);
  };
  
  const handleDeleteWatch = async (id) => {
    if (window.confirm('Bu saati silmek istediğinizden emin misiniz?')) {
      const watchDoc = doc(db, 'watches', id);
      await deleteDoc(watchDoc);
      fetchWatches(); // Refresh the list
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWatchForm({
      ...watchForm,
      [name]: value
    });
  };

  const handleImageFile = async (fieldName, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError('');
    setUploadSuccessMessage('');
    setUploadingKey(fieldName);
    setUploadProgress(5);
    const slotLabel =
      fieldName === 'image1' ? 'Kapak görseli' : `Resim ${fieldName.replace('image', '')}`;
    try {
      const url = await uploadWatchImage(file, {
        onProgress: (pct) => setUploadProgress(pct),
      });
      setWatchForm((prev) => ({ ...prev, [fieldName]: url }));
      setUploadSuccessMessage(`${slotLabel} başarıyla yüklendi.`);
    } catch (err) {
      console.error(err);
      setUploadError(
        err.message ||
          'Yükleme başarısız. Firebase Storage açık ve kurallar doğru mu kontrol edin.'
      );
    } finally {
      setUploadingKey(null);
      setUploadProgress(null);
    }
  };
  
  const handleSubmitWatch = async (e) => {
    e.preventDefault();

    if (!watchForm.image1?.trim()) {
      setUploadError('Kapak görseli zorunlu: dosya yükleyin veya URL girin.');
      return;
    }

    const formattedWatch = {
      name: watchForm.name,
      description: watchForm.description,
      longDescription: watchForm.longDescription,
      price: watchForm.price.includes('$') ? watchForm.price : `$${watchForm.price}`,
      images: [
        watchForm.image1,
        watchForm.image2,
        watchForm.image3,
        watchForm.image4
      ].filter(Boolean)
    };

    if (isEditing) {
      const watchDoc = doc(db, 'watches', currentWatch.id);
      await updateDoc(watchDoc, formattedWatch);
    } else {
      await addDoc(collection(db, 'watches'), {
        ...formattedWatch,
        createdAt: new Date(),
      });
    }

    fetchWatches(); // Refresh the list
    setShowForm(false);
  };
  
  const handleCancelForm = () => {
    setShowForm(false);
    setUploadError('');
    setUploadSuccessMessage('');
    setWatchForm({
      name: '',
      description: '',
      longDescription: '',
      price: '',
      image1: '',
      image2: '',
      image3: '',
      image4: ''
    });
  };
  
  if (!isAuthenticated) {
    return (
      <div className="page admin-page">
        <div className="login-container">
          <motion.div
            className="login-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2>Yönetim</h2>
            <p className="login-eyebrow">KAIN · güvenli giriş</p>
            {loginError && <div className="login-error">{loginError}</div>}
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="username">Kullanıcı Adı</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Şifre</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="login-button">Giriş Yap</button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="page admin-page">
      <div className="container">
        <div className="admin-header">
          <h1>Yönetim Paneli</h1>
          <button onClick={handleLogout} className="logout-button">Çıkış Yap</button>
        </div>
        
        <div className="admin-content">
          <div className="admin-sidebar">
            <div className="sidebar-title">İşlemler</div>
            <ul className="sidebar-menu">
              <li className="active">Saatler</li>
            </ul>
            <button onClick={handleAddWatch} className="add-button">
              + Yeni Saat Ekle
            </button>
          </div>
          
          <div className="admin-main">
            {showForm ? (
              <div className="watch-form-container">
                <h2>{isEditing ? 'Saati Düzenle' : 'Yeni Saat Ekle'}</h2>
                <form onSubmit={handleSubmitWatch} className="watch-form">
                  <div className="form-grid">
                    {uploadSuccessMessage ? (
                      <div className="admin-upload-success admin-form-span" role="status">
                        {uploadSuccessMessage}
                      </div>
                    ) : null}
                    {uploadError ? (
                      <div className="admin-upload-banner admin-form-span">{uploadError}</div>
                    ) : null}
                    <p className="admin-upload-hint admin-form-span">
                      JPG, PNG, WebP veya GIF yükleyin; iPhone’da HEIC yerine en uyumlu sonuç için fotoğrafı JPEG olarak dışa aktarın. İsterseniz harici görsel linki de yapıştırabilirsiniz.
                    </p>
                    <div className="form-group">
                      <label htmlFor="name">Saat Adı</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={watchForm.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="price">Fiyat ($)</label>
                      <input
                        type="text"
                        id="price"
                        name="price"
                        value={watchForm.price}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label htmlFor="description">Kısa Açıklama</label>
                      <input
                        type="text"
                        id="description"
                        name="description"
                        value={watchForm.description}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group full-width">
                      <label htmlFor="longDescription">Detaylı Açıklama</label>
                      <textarea
                        id="longDescription"
                        name="longDescription"
                        rows="5"
                        value={watchForm.longDescription}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    
                    {['image1', 'image2', 'image3', 'image4'].map((key, idx) => (
                      <div className="form-group admin-image-field" key={key}>
                        <label htmlFor={key}>
                          {idx === 0 ? 'Kapak görseli' : `Resim ${idx + 1}`} {idx === 0 ? '(zorunlu)' : '(isteğe bağlı)'}
                        </label>
                        <div className="admin-image-toolbar">
                          <label className="admin-file-pick">
                            <input
                              type="file"
                              id={`file-${key}`}
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              disabled={!!uploadingKey}
                              onChange={(ev) => handleImageFile(key, ev)}
                            />
                            {uploadingKey === key
                              ? uploadProgress != null
                                ? `Yükleniyor %${uploadProgress}`
                                : 'Gönderiliyor…'
                              : 'Dosyadan yükle'}
                          </label>
                        </div>
                        <input
                          type="text"
                          id={key}
                          name={key}
                          value={watchForm[key]}
                          onChange={handleInputChange}
                          placeholder="veya görsel linkini yapıştırın (https://…)"
                          className="admin-image-url"
                          autoComplete="off"
                        />
                        {watchForm[key] ? (
                          <img
                            src={withCropParams(watchForm[key], 200)}
                            alt=""
                            className="admin-image-preview"
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={handleCancelForm} className="cancel-button">
                      İptal
                    </button>
                    <button type="submit" className="save-button">
                      {isEditing ? 'Güncelle' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="watches-management">
                <h2>Saatler</h2>
                {loading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                  </div>
                ) : watches.length > 0 ? (
                  <table className="watches-table">
                    <thead>
                      <tr>
                        <th>Resim</th>
                        <th>Saat Adı</th>
                        <th>Açıklama</th>
                        <th>Fiyat</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {watches.map(watch => (
                        <tr key={watch.id}>
                          <td>
                            <img
                              src={watch.images?.[0] ? withCropParams(watch.images[0], 120) : ''}
                              alt={watch.name}
                            />
                          </td>
                          <td>{watch.name}</td>
                          <td>{watch.description}</td>
                          <td>{watch.price}</td>
                          <td>
                            <button onClick={() => handleEditWatch(watch)} className="edit-button">
                              Düzenle
                            </button>
                            <button onClick={() => handleDeleteWatch(watch.id)} className="delete-button">
                              Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-watches">
                    Henüz hiç saat eklenmemiş. "Yeni Saat Ekle" butonuna tıklayarak saat ekleyebilirsiniz.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage; 